import { useEffect } from 'react';
import { actions } from '@/lib/actions';
import { gStore, isTyping, runComposeContext, runEscape, runViewKey, trapTab } from '@/lib/keys';
import { anyLayerOpen, togglePalette, openPalette } from '@/lib/overlays';
import { goTo, routeStore, type ViewId } from '@/lib/router';
import { openShortcuts, shortcutsOpen } from './Shortcuts';

// The keys that work everywhere. Order matters:
// 1. Cmd/Ctrl K toggles the palette, Escape closes the top-most layer and Tab stays inside it, always.
// 2. Everything else pauses while you type in a field, and with Cmd, Ctrl or Alt held (the browser's own keys).
// 3. "G then a letter" goes to a view; ? opens the shortcut sheet, even over a dialog.
// 4. With a dialog, the palette or the composer open, nothing further reacts.
// 5. / searches, E writes an email, C replies where there is something to reply to.
// 6. With a record or the admin console open, the list keys (J, K, X, Enter) stay with the sheet.

const G_MAP: Record<string, ViewId> = { t: 'today', i: 'inbox', p: 'people', c: 'companies', d: 'deals', f: 'finder', a: 'activity' };

let hintTimer: ReturnType<typeof setTimeout> | undefined;
let expireTimer: ReturnType<typeof setTimeout> | undefined;

function endG(): void {
  clearTimeout(hintTimer);
  clearTimeout(expireTimer);
  if (gStore.get().pending || gStore.get().hint) gStore.set({ pending: false, hint: false });
}

function startG(): void {
  endG();
  gStore.set({ pending: true, hint: false });
  hintTimer = setTimeout(() => {
    if (gStore.get().pending) gStore.set({ hint: true });
  }, 350);
  expireTimer = setTimeout(endG, 1400);
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.defaultPrevented) return;
  const k = e.key;

  if ((e.metaKey || e.ctrlKey) && !e.altKey && (k === 'k' || k === 'K')) {
    e.preventDefault();
    endG();
    togglePalette();
    return;
  }
  if (k === 'Escape') {
    endG();
    if (runEscape()) e.preventDefault();
    return;
  }
  if (k === 'Tab') {
    trapTab(e);
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if (isTyping(e.target)) return;

  const lk = k.length === 1 ? k.toLowerCase() : k;
  if (gStore.get().pending) {
    endG();
    const view = G_MAP[lk];
    if (view) {
      e.preventDefault();
      goTo(view);
      return;
    }
  }
  if (k === '?') {
    e.preventDefault();
    if (!shortcutsOpen()) openShortcuts();
    return;
  }
  if (anyLayerOpen()) return;

  const route = routeStore.get().route;
  if (k === '/') {
    e.preventDefault();
    openPalette('');
    return;
  }
  if (lk === 'g' && !e.repeat) {
    startG();
    return;
  }
  if (lk === 'e') {
    e.preventDefault();
    if (!runComposeContext()) actions.compose();
    return;
  }
  if (lk === 'c' && (route.view === 'inbox' || route.record)) {
    e.preventDefault();
    if (!runComposeContext()) actions.compose();
    return;
  }
  if (route.record || route.admin) return;

  if (lk === 'j' || lk === 'k' || lk === 'x') {
    if (runViewKey(lk, e)) e.preventDefault();
    return;
  }
  if (k === 'Enter') {
    // Enter on a focused button or link does that button's job; with nothing focused it opens the current row.
    const active = document.activeElement;
    if (!active || active === document.body || active.id === 'main') {
      if (runViewKey('enter', e)) e.preventDefault();
    }
  }
}

/** Listens for the app-wide keys while signed in. */
export function useGlobalKeys(): void {
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', endG);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', endG);
      endG();
    };
  }, []);
}
